import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ParagraphText, UBButton } from "../../../../Libraries";
import { TitleAndActionBox } from "../../../common";
import { Box, Grid } from "@mui/material";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useNavigate, useLocation } from "react-router-dom";
import BillPaymentForm from "./BillPaymentForm";
import BillReviewSection from "./BillReviewSection";
import { useWalletContext } from "../../../../context/WalletContext";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { Sync } from "../../../../SyncService";
import { useAuthContext } from "../../../../context/AuthContext";
import { uuid } from "../../../../constants";
import { handleApi } from "../../../../apis";
import {
  GETWALLETBILLINQUERY,
  GETWALLETBILLPAYMENT,
  POSTWALLETBILLINQUERY,
  POSTWALLETBILLPAYMENT,
} from "../../../../apis/configuration";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import BusinessLoader from "../../../common/BusinessLoader";
import withObservables from "@nozbe/with-observables";
import { getCustomerOfBusiness } from "../../../../database/helpers/customer";
import formStyles from "../../SaleExpense/SaleExpenseForm/styles.module.css";
import bpStyles from "./BillPayments.module.css";
import { v4 as uuidv4 } from "uuid";
import { EnterIcon, BillPlaceholderIcon } from "../../../../assets/icons/icon";
import { getCategoriesByType } from "../../../../utils";
import VerifyPinModal from "../../../common/Modals/VerifyPinModal";

const BillPayments = ({ customers }) => {
  const [I18n] = useContext(LanguageContext);

  const navigate = useNavigate();
  const { state: locationState } = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);
  const [state, setState] = useState(() => ({
    billingConsumerNumber: "",
    billingCompany: "",
    billingCompanyId: "",
    billingCategory: locationState?.tab || "",
    contactName: locationState?.prefilledCustomerName || "",
    expenseCategory: "",
    expenseCategoryId: "",
    paymentStatus: "Paid",
    paymentMode: "Cash",
  }));
  const [billInqueryData, setBillInqueryData] = useState(null);
  const [billBusinessList, setBillBusinessList] = useState([]);
  const [customer, setCustomer] = useState(null);
  const idempotencyKey = useRef(uuidv4());
  const lastSubmittedRef = useRef({ company: "", consumerNumber: "" });
  const prePopulatedRef = useRef(false);

  const [failedIcons, setFailedIcons] = useState({});
  
  const markIconFailed = (key) =>
    setFailedIcons((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  const {
    fetchWalletData,
    handleFetchPopularBills,
    popularBills,
    popularBillsLoading,
    walletData,
    handleSearchCustomerBill,
    fetchBillList,
    handleGetWalletBalance,
    fetchAccountBalanceLoading,
  } = useWalletContext();

  const balance = walletData?.current_balance || 0;
  const earningRules = walletData?.biller_earning_rules || {};
  const partnerStatus = walletData?.account_info?.partner_status || "";
  const availableBills = walletData?.available_billers || [];
  const { showSnackbar } = useSnackbar();
  const { user } = useAuthContext();

  useEffect(() => {
    handleFetchPopularBills(navigate);

    if (!availableBills || availableBills.length == 0) {
      fetchBillList(navigate);
    }

    return () => {
      isMounted.current = false;
      if (inqTimeoutId.current) clearTimeout(inqTimeoutId.current);
      if (walletRechargeTimeoutId.current)
        clearTimeout(walletRechargeTimeoutId.current);
    };
  }, []);

  useEffect(() => {
    if (locationState?.tab) {
      handleCategoryChange(locationState?.tab);
    }
  }, [locationState]);

  // Auto-select the WatermelonDB customer when navigated from a contact detail page
  useEffect(() => {
    if (!locationState?.prefilledCustomerName || customer || !customers?.length)
      return;
    const found = customers.find(
      (c) =>
        (c.name || "").toLowerCase() ===
        locationState.prefilledCustomerName.toLowerCase(),
    );
    if (found) setCustomer(found);
  }, [customers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-populate from navigation state (e.g. coming from consumer bills listing)
  useEffect(() => {
    if (
      prePopulatedRef.current ||
      !locationState?.billingCompany ||
      !availableBills?.length
    )
      return;

    let foundCategory = "";
    let foundBusinessList = [];
    let foundCompany = null;
    for (const cat of availableBills) {
      const match = (cat.business_list || []).find(
        (c) =>
          c.name?.toLowerCase() === locationState.billingCompany?.toLowerCase(),
      );
      if (match) {
        foundCategory = cat.business;
        foundBusinessList = cat.business_list;
        foundCompany = match;
        break;
      }
    }
    if (!foundCategory) return;

    prePopulatedRef.current = true;
    setBillBusinessList(foundBusinessList);
    setState((prev) => ({
      ...prev,
      billingCategory: foundCategory,
      billingCompany: locationState.billingCompany,
      billingCompanyId: foundCompany?.id || "",
      billingConsumerNumber: locationState.consumerNumber || "",
    }));
  }, [availableBills]); // eslint-disable-line react-hooks/exhaustive-deps

  const allBillsFlat = useMemo(() => {
    const result = [];
    (availableBills || []).forEach((cat) => {
      (cat.business_list || []).forEach((company) => {
        result.push({
          name: company.name || "",
          icon: company.icon || "",
          id: company.id || "",
          category: cat.business || "",
          businessList: cat.business_list || [],
        });
      });
    });
    return result;
  }, [availableBills]);

  const categoryOptions = useMemo(
    () =>
      (availableBills || []).map((item) => {
        const iconKey = `category_${item.business}`;
        return {
          label: item.business,
          value: item.business,
          name: item.business,
          icon: item.business_icon,
          img:
            item.business_icon && !failedIcons[iconKey] ? (
              <img
                src={item.business_icon}
                alt={item.business}
                width={20}
                height={20}
                onError={() => markIconFailed(iconKey)}
              />
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
                <BillPlaceholderIcon width={14} height={14} />
              </Box>
            ),
        };
      }),
    [availableBills, failedIcons],
  );

  const companyOptions = useMemo(
    () =>
      (billBusinessList || []).map((b) => {
        const iconKey = `company_${b.id || b.name}`;
        return {
          label: b.name || "",
          value: (b.name || "").trim(),
          name: b.name || "",
          img:
            b.icon && !failedIcons[iconKey] ? (
              <img
                src={b.icon}
                alt={b.name}
                width={20}
                height={20}
                onError={() => markIconFailed(iconKey)}
              />
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
                <BillPlaceholderIcon width={14} height={14} />
              </Box>
            ),
          icon: b.icon || "",
          id: b.id || "",
        };
      }),
    [billBusinessList, failedIcons],
  );

  const selectedCompanyIcon = useMemo(() => {
    const found = billBusinessList.find((b) => b.name === state.billingCompany);
    return found?.icon || "";
  }, [billBusinessList, state.billingCompany]);

  const earnPoints = useMemo(() => {
    if (!state.billingCompanyId || !partnerStatus) return 0;
    return earningRules?.[String(state.billingCompanyId)]?.[partnerStatus] || 0;
  }, [earningRules, state.billingCompanyId, partnerStatus]);

  const categoryList = useMemo(() => getCategoriesByType("bill_payment"), []);

  const handleCategoryChange = (categoryName) => {
    const billObj = availableBills.find((i) => i.business === categoryName);
    setBillBusinessList(billObj?.business_list || []);
    setState((e) => ({
      ...e,
      billingCategory: categoryName,
      billingCompany: "",
      billingCompanyId: "",
      billingConsumerNumber: "",
    }));
  };

  const handlePopularBillSelect = (bill) => {
    let foundCategory = "";
    let foundBusinessList = [];
    let foundCompany = null;
    for (const cat of availableBills) {
      const match = (cat.business_list || []).find(
        (c) => c.name?.toLowerCase() === bill.name?.toLowerCase(),
      );
      if (match) {
        foundCategory = cat.business;
        foundBusinessList = cat.business_list;
        foundCompany = match;
        break;
      }
    }
    if (bill.category && !foundCategory) {
      foundCategory = bill.category;
      foundBusinessList = bill.businessList || [];
    }
    setBillBusinessList(foundBusinessList);
    setState((e) => ({
      ...e,
      billingCategory: foundCategory,
      billingCompany: bill.name,
      billingCompanyId: foundCompany?.id || bill.id || "",
      billingConsumerNumber: "",
    }));
  };

  const isFormDisabled = !(
    state.billingConsumerNumber.length >= 5 &&
    state.billingCompany &&
    state.billingCategory
  );

  const isPaidBill =
    (billInqueryData?.bill_status || "").toLowerCase() === "paid" ||
    (billInqueryData?.bill_status || "").toLowerCase() === "already paid";

  const isReviewDisabled =
    !fetchAccountBalanceLoading &&
    !isPaidBill &&
    balance < (billInqueryData?.bill_amount || 0);

  const isDetailView = Boolean(billInqueryData);

  useEffect(() => {
    if (isDetailView) {
      handleGetWalletBalance(navigate).finally(() => setIsLoading(false));
    }
  }, [isDetailView]);

  const onBillInqueryPost = async () => {
    try {
      triggerLogEvent(analytics, "w_earn_bill_continue");
      setIsLoading(true);
      const payload = {
        billing_consumer_number: state.billingConsumerNumber,
        billing_company: state.billingCompany,
      };
      const response = await handleApi(
        POSTWALLETBILLINQUERY,
        payload,
        null,
        navigate,
      );
      if (response?.success) {
        // Only on a successful inquiry do we commit: if company or consumer changed,
        // mint a new idempotency key so the upcoming payment is treated as a fresh attempt.
        if (
          state.billingCompany !== lastSubmittedRef.current.company ||
          state.billingConsumerNumber !==
            lastSubmittedRef.current.consumerNumber
        ) {
          idempotencyKey.current = uuidv4();
        }
        lastSubmittedRef.current = {
          company: state.billingCompany,
          consumerNumber: state.billingConsumerNumber,
        };
        onBillInqueryGet(response?.billing_key);
      } else {
        setIsLoading(false);
        showSnackbar({
          message: response?.message || I18n.bill_not_found,
          severity: "error",
          icon: "",
        });
      }
    } catch (error) {
      setIsLoading(false);
      showSnackbar({
        message: error?.response?.data?.message || I18n.error_fetching_bill,
        severity: "error",
        icon: "",
      });
    }
  };

  const inqTimeoutId = useRef(null);
  const isMounted = useRef(true);
  const onBillInqueryGet = async (id) => {
    try {
      const response = await handleApi(
        GETWALLETBILLINQUERY,
        { billing_key: id },
        null,
        navigate,
      );
      if (!isMounted.current) return;
      if (response?.success) {
        if (inqTimeoutId.current) clearTimeout(inqTimeoutId.current);
        setBillInqueryData(response);
      } else {
        setIsLoading(true);
        inqTimeoutId.current = setTimeout(() => onBillInqueryGet(id), 3000);
      }
    } catch (error) {
      if (!isMounted.current) return;
      if (inqTimeoutId.current) clearTimeout(inqTimeoutId.current);
      setIsLoading(false);
      showSnackbar({
        message: error?.response?.data?.message || I18n.error_fetching_bill,
        severity: "error",
        icon: "",
      });
    }
  };

  const handlePaybillClick = () => {
    if (balance < (billInqueryData?.bill_amount || 0)) {
      showSnackbar({
        message: I18n.insufficient_balance_please_try_different_amount,
        severity: "error",
        icon: "",
      });
      return;
    }
    setIsVerifyOpen(true);
  };

  const handlePaybill = async () => {
    setIsLoading(true);
    triggerLogEvent(analytics, "w_earn_bill_pay");
    try {
      const payload = {
        billing_consumer_number: state.billingConsumerNumber,
        billing_company: state.billingCompany,
        customer_name: customer?.name || "",
        customer_phone: customer?.phone || "",
        idempotency_key: idempotencyKey.current,
        category_id: state.expenseCategoryId || "",
      };
      const response = await handleApi(
        POSTWALLETBILLPAYMENT,
        payload,
        null,
        navigate,
      );
      if (response?.success) {
        fetchWalletRechargeTransaction(response?.billing_key);
      } else {
        setIsLoading(false);
        showSnackbar({
          message: response?.message || I18n.bill_payment_failed,
          severity: "error",
          icon: "",
        });
      }
    } catch (error) {
      setIsLoading(false);
      showSnackbar({
        message: error?.response?.data?.message || I18n.error_in_bill_payment,
        severity: "error",
        icon: "",
      });
    }
  };

  const walletRechargeTimeoutId = useRef(null);
  const fetchWalletRechargeTransaction = async (id) => {
    try {
      const response = await handleApi(
        GETWALLETBILLPAYMENT,
        { billing_key: id },
        null,
        navigate,
      );
      if (response?.status === "COMPLETED") {
        if (walletRechargeTimeoutId.current)
          clearTimeout(walletRechargeTimeoutId.current);
        const transaction = response || {};
        const transaction_details = transaction?.transaction_details || {};
        const data = {
          amount: Math.abs(transaction?.amount),
          to: `${transaction_details?.bill_company}(****${(transaction_details?.consumer_number || "").slice(-4)})`,
          logo: transaction?.icon,
          type: transaction?.type,
          date: transaction_details?.created_at,
          commission: transaction?.commission,
        };
        try {
          if (state.paymentStatus === "Unpaid" && customer) {
            await customer.createPayment({
              uid: uuid(),
              amount: Number(Math.abs(transaction?.amount)),
              payment_mode: "Udhaar",
              date: new Date(),
              udhaar_note: I18n.bill_payment,
              imageURL: null,
              outstanding:
                (customer.total_outstanding_payment || 0) +
                Math.abs(transaction?.amount),
              external_type: "bills",
            });
            Sync(user?.phone_number, navigate);
          }
        } catch {}
        triggerLogEvent(analytics, "w_earn_bill_confirm");
        fetchWalletData({ navigate });
        if (!isMounted.current) return;
        setIsLoading(false);
        navigate(`/UCash/BillPayments/TransactionSuccess/${id}`, {
          state: { data, transaction: transaction_details },
        });
      } else if (response?.status === "FAILED") {
        if (walletRechargeTimeoutId.current)
          clearTimeout(walletRechargeTimeoutId.current);
        if (!isMounted.current) return;
        setIsLoading(false);
        showSnackbar({
          message: response?.message || I18n.transaction_failed,
          severity: "error",
          icon: "",
        });
      } else {
        if (!isMounted.current) return;
        walletRechargeTimeoutId.current = setTimeout(
          () => fetchWalletRechargeTransaction(id),
          5000,
        );
      }
    } catch (error) {
      if (!isMounted.current) return;
      if (walletRechargeTimeoutId.current) {
        clearTimeout(walletRechargeTimeoutId.current);
      }
      setIsLoading(false);
      showSnackbar({
        message: error?.response?.data?.message || I18n.error_in_bill_payment,
        severity: "error",
        icon: "",
      });
    }
  };

  return (
    <Fragment>
      <Grid
        container
        className={`${formStyles.root} ${formStyles.SaleExpenseFormWrapper} ${bpStyles.billPaymentsWrapper}`}
      >
        <BusinessLoader loadBusiness={isLoading} />
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: isDetailView ? I18n.confirm_payment : I18n.pay_bill,
              isBack: true,
              onBack: isLoading
                ? () => {}
                : isDetailView
                  ? () => setBillInqueryData(null)
                  : () => navigate(-1),
              id: "bill_payments",
            }}
          />
          <Box className={formStyles.formBodyGrid}>
            <Box className={formStyles.formColumn}>
              <Box
                className={`${formStyles.formBody} expenseFormBody ${bpStyles.billPaymentsFormBody}`}
              >
                {isDetailView ? (
                  <BillReviewSection
                    I18n={I18n}
                    billInqueryData={billInqueryData}
                    icon={selectedCompanyIcon}
                    isPaidBill={isPaidBill}
                    customers={customers}
                    state={state}
                    setState={setState}
                    setCustomer={setCustomer}
                    categoryList={categoryList}
                    balance={balance}
                    balanceLoading={fetchAccountBalanceLoading}
                    earnPoints={earnPoints}
                  />
                ) : (
                  <BillPaymentForm
                    I18n={I18n}
                    popularBills={popularBills}
                    popularBillsLoading={popularBillsLoading}
                    categoryOptions={categoryOptions}
                    companyOptions={companyOptions}
                    allBillsFlat={allBillsFlat}
                    state={state}
                    setState={setState}
                    onCategoryChange={handleCategoryChange}
                    onPopularBillSelect={handlePopularBillSelect}
                    onSearchCustomerBill={handleSearchCustomerBill}
                    navigate={navigate}
                    companyIcon={selectedCompanyIcon}
                    isFormDisabled={isFormDisabled}
                    onEnterPress={onBillInqueryPost}
                  />
                )}

                {isDetailView && isPaidBill ? (
                  <UBButton
                    id="bill_go_home_btn"
                    title={<ParagraphText>{I18n.go_to_wallet}</ParagraphText>}
                    className={`buttonPrimary saveSaleButton ${bpStyles.billPaymentsPrimaryAction}`}
                    onClick={() => navigate("/UCash")}
                  />
                ) : isDetailView ? (
                  <UBButton
                    id="bill_payments_pay_btn"
                    title={<ParagraphText>{I18n.pay_bill}</ParagraphText>}
                    loading={isLoading}
                    disabled={isReviewDisabled || isLoading}
                    className={`buttonPrimary saveSaleButton ${bpStyles.billPaymentsPrimaryAction}`}
                    onClick={handlePaybillClick}
                  />
                ) : (
                  <UBButton
                    id="bill_payments_next_btn"
                    title={<ParagraphText>{I18n.next}</ParagraphText>}
                    iconComponent={<EnterIcon />}
                    iconPlacement="right"
                    loading={isLoading}
                    disabled={isFormDisabled || isLoading}
                    className={`buttonPrimary saveSaleButton ${bpStyles.billPaymentsPrimaryAction}`}
                    onClick={onBillInqueryPost}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <VerifyPinModal
        open={isVerifyOpen}
        handleClose={() => setIsVerifyOpen(false)}
        amount={billInqueryData?.bill_amount || 0}
        onVerified={handlePaybill}
        forgotPinAnalyticsEvent="w_earn_bill_forgot_pin"
      />
    </Fragment>
  );
};

const enhance = withObservables([], () => ({
  customers: getCustomerOfBusiness(
    typeof window !== "undefined"
      ? localStorage.getItem("business_id") || ""
      : "",
  ),
}));

export default enhance(BillPayments);
